import { supabase, unwrapFunctionError } from '../supabase';
import { throwIfQuota } from './content';
import type { MarketSymbol, Quote } from '../types';

export type Period = '1g' | '1h' | '1a' | '3a' | '1y';
export const PERIODS: Period[] = ['1g', '1h', '1a', '3a', '1y'];
export const TICKER_SYMBOLS = ['SPX', 'IXIC', 'XU100', 'BTC-USD', 'ETH-USD', 'XAUUSD', 'BRENT', 'USD-TRY', 'EUR-USD'];

let symbolsP: Promise<MarketSymbol[]> | null = null;
export function getSymbols(): Promise<MarketSymbol[]> {
  symbolsP ??= (async () => {
    const { data, error } = await supabase
      .from('market_symbols')
      .select('symbol,kind,display_name,sort')
      .eq('enabled', true)
      .order('sort');
    if (error) {
      symbolsP = null;
      throw error;
    }
    return (data ?? []) as MarketSymbol[];
  })();
  return symbolsP;
}

export async function getQuotes(): Promise<Record<string, Quote>> {
  const { data, error } = await supabase.functions.invoke('market-quotes', { body: {} });
  if (error) throw await unwrapFunctionError(error);
  return ((data as { quotes?: Record<string, Quote> })?.quotes ?? {}) as Record<string, Quote>;
}

export type Point = { ts: number; close: number };

export async function getHistory(symbol: string, period: Period): Promise<Point[]> {
  const { data, error } = await supabase.functions.invoke('market-history', { body: { symbol, period } });
  if (error) throw await unwrapFunctionError(error);
  const series = (data as { series?: { ts: string; close: number }[] })?.series ?? [];
  return series
    .map((p) => ({ ts: new Date(p.ts).getTime(), close: Number(p.close) }))
    .filter((p) => Number.isFinite(p.close));
}

/** Sembol adından haber araması için anahtar kelime (ör. "Bitcoin / USD" → "Bitcoin"). */
export function newsQueryFor(sym: MarketSymbol): string {
  return sym.display_name.split(/[/(]/)[0].trim() || sym.symbol;
}

export type MChat = { role: 'user' | 'assistant'; text: string };

export async function fetchMarketChat(userId: string, symbol: string): Promise<MChat[]> {
  const { data } = await supabase
    .from('market_chat_messages')
    .select('role,text,created_at')
    .eq('user_id', userId)
    .eq('symbol', symbol)
    .order('created_at');
  return ((data ?? []) as MChat[]).map((m) => ({ role: m.role, text: m.text }));
}

export async function sendMarketChat(userId: string, symbol: string, history: MChat[], text: string, lang: string): Promise<string> {
  const messages = [...history, { role: 'user' as const, text }].slice(-12).map((m) => ({ role: m.role, content: m.text }));
  const { data, error } = await supabase.functions.invoke('market-chat', { body: { symbol, messages, lang } });
  if (error) throw await unwrapFunctionError(error);
  throwIfQuota(data);
  const reply = (data as { reply?: string })?.reply;
  if (!reply) throw new Error('no reply');
  await supabase.from('market_chat_messages').insert([
    { user_id: userId, symbol, role: 'user', text },
    { user_id: userId, symbol, role: 'assistant', text: reply },
  ]);
  return reply;
}
